import { useMemo } from "react";

import { TableProps } from "@nens/chakra/components";

import { UseRouterKeyAddon, useRouterTableData } from "@/hooks/use-router-table-data";

const getPageFunctions = ({
  page,
  numPages,
  setPage,
}: {
  page: number;
  numPages: number | undefined;
  setPage: (page: number) => void;
}) => {
  const toPage1 = page > 1 ? () => setPage(1) : undefined;
  const toPrevious = page > 1 ? () => setPage(page - 1) : undefined;
  const toNext = numPages && page < numPages ? () => setPage(page + 1) : undefined;
  const toLastPage = numPages && page < numPages ? () => setPage(numPages) : undefined;
  return { toPage1, toPrevious, toNext, toLastPage };
};

export const useAllFetchedPagination = <T>(
  keyAddon: UseRouterKeyAddon | false,
  tableData: TableProps<T>["tableData"]
) => {
  const {
    page,
    setPage,
    pageSize: itemsPerPage,
    setPageSize: setItemsPerPage,
  } = useRouterTableData(keyAddon ? { keyAddon } : { hasSearchParams: false });

  const numPages = tableData.length > 10 ? Math.ceil(tableData.length / itemsPerPage) : undefined;
  const pageFunctions = getPageFunctions({ page, numPages, setPage });
  const paginatedTableData = useMemo(() => {
    return [...tableData].splice(itemsPerPage * (page - 1), itemsPerPage);
  }, [itemsPerPage, page, tableData]);

  return {
    itemsPerPage,
    setItemsPerPage,
    page,
    numPages,
    ...pageFunctions,
    paginatedTableData,
  };
};

export interface ApiListResponse<T> {
  count: number;
  next?: string | null;
  previous?: string | null;
  results: T[];
}

export const useQueryPagination = <T>({
  queryResponse,
  page,
  setPage,
}: {
  queryResponse: ApiListResponse<T> | undefined;
  page: number;
  setPage: (page: number) => void;
}) => {
  const itemsPerPage = queryResponse ? queryResponse.results.length : 0;
  const count = queryResponse ? queryResponse.count : 0;
  const numPages = count > 10 ? Math.ceil(count / itemsPerPage) : undefined;
  const pageFunctions = getPageFunctions({ page, numPages, setPage });

  return {
    itemsPerPage,
    numPages,
    page,
    ...pageFunctions,
  };
};
